import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import usePersistedState from "@/lib/usePersistedState";
import { PageHeader } from "@/components/Layout";
import PeriodPicker, { presetRange } from "@/components/PeriodPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, Eye, Mail } from "lucide-react";
import { inr, formatDate } from "@/lib/utils2";
import EmailDialog from "@/components/EmailDialog";

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [filter, setFilter] = usePersistedState("filters:bills", { bill_type: "all" });
  const [period, setPeriod] = usePersistedState("period:bills", () => presetRange("this-fy"));
  const [parties, setParties] = useState([]);
  const [settings, setSettings] = useState({});

  const load = async () => {
    const params = { date_from: period.from, date_to: period.to };
    if (filter.bill_type !== "all") params.bill_type = filter.bill_type;
    setBills((await api.get("/bills", { params })).data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter.bill_type, period.from, period.to]);
  useEffect(() => {
    api.get("/parties").then((r) => setParties(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const partyMap = useMemo(
    () => Object.fromEntries(parties.map((p) => [p.id, p])), [parties],
  );

  const buildEmailDefaults = (b) => {
    const party = partyMap[b.party_id] || {};
    const company = settings?.company_name || "TULSI TRADING AND LOGISTICS";
    const typeLabel = b.bill_type === "combined"
      ? "combined tax invoice"
      : b.bill_type === "transport"
      ? "transport bill"
      : "material tax invoice";
    return {
      to: party.email || "",
      subject: `${typeLabel[0].toUpperCase() + typeLabel.slice(1)} ${b.bill_number} — ${company}`,
      message:
        `Dear ${party.name || b.party_name || "Sir/Madam"},\n\n` +
        `Please find attached ${typeLabel} for your reference:\n\n` +
        `   • Bill Number : ${b.bill_number}\n` +
        `   • Bill Date   : ${formatDate(b.bill_date)}\n` +
        `   • Amount      : ₹ ${Number(b.total_amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n\n` +
        `The invoice PDF is attached to this email.\n\n` +
        `Kindly acknowledge receipt.\n\n` +
        `Regards,\n${company}`,
    };
  };

  return (
    <div className="p-6 md:p-10 lg:p-12 max-w-[1500px] mx-auto" data-testid="bills-page">
      <PageHeader
        title="Bills"
        subtitle="All material & transport invoices. Click a row to view, edit fields, and print."
        action={
          <Link to="/bills/new">
            <Button data-testid="create-bill-btn" className="rounded-sm bg-brand-forest hover:bg-brand-forestDark text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Create Bill
            </Button>
          </Link>
        }
      />

      <Card className="rounded-sm border-brand-line shadow-none p-4 mb-5">
        <div className="flex items-end gap-4 flex-wrap">
          <PeriodPicker value={period} onChange={(p) => setPeriod({ from: p.from, to: p.to })}
            defaultPreset="this-fy" testIdPrefix="bills-period" />
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-brand-muted">Type</Label>
            <Select value={filter.bill_type} onValueChange={(v) => setFilter({ ...filter, bill_type: v })}>
              <SelectTrigger className="mt-1.5 w-44 h-10 rounded-sm bg-white" data-testid="bills-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Bills</SelectItem>
                <SelectItem value="material">Material Bills</SelectItem>
                <SelectItem value="transport">Transport Bills</SelectItem>
                <SelectItem value="combined">Combined Bills</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border-brand-line shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
            <tr>
              <th className="text-left px-5 py-3">Bill No</th>
              <th className="text-left px-3 py-3">Date</th>
              <th className="text-left px-3 py-3">Party</th>
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-right px-3 py-3">Subtotal</th>
              <th className="text-right px-3 py-3">GST</th>
              <th className="text-right px-3 py-3">Total</th>
              <th className="text-right px-3 py-3">Received</th>
              <th className="text-right px-3 py-3">Balance</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {bills.length === 0 && <tr><td colSpan={10} className="px-5 py-10 text-center text-brand-muted text-sm">No bills yet. <Link to="/bills/new" className="text-brand-forest underline">Create one →</Link></td></tr>}
            {bills.map((b) => (
              <tr key={b.id} className="border-t border-brand-line hover:bg-secondary/40" data-testid={`bill-row-${b.id}`}>
                <td className="px-5 py-3 font-mono text-xs">{b.bill_number}</td>
                <td className="px-3 py-3">{formatDate(b.bill_date)}</td>
                <td className="px-3 py-3 font-medium">{b.party_name}</td>
                <td className="px-3 py-3">
                  <Badge variant="outline" className={`rounded-sm uppercase text-[10px] tracking-[0.1em] ${
                    b.bill_type === "material" ? "border-brand-forest text-brand-forest" :
                    b.bill_type === "combined" ? "border-brand-sand bg-brand-sand/10 text-brand-ink" :
                    "border-brand-sand text-brand-sand"
                  }`}>
                    {b.bill_type}
                  </Badge>
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{inr(b.subtotal)}</td>
                <td className="px-3 py-3 text-right tabular-nums">{b.gst_percent}%</td>
                <td className="px-3 py-3 text-right tabular-nums font-semibold">{inr(b.total_amount)}</td>
                <td className="px-3 py-3 text-right tabular-nums text-brand-forest">{inr(b.received_total)}</td>
                <td className={`px-3 py-3 text-right tabular-nums font-medium ${b.balance_due > 0 ? "text-brand-terracotta" : "text-brand-muted"}`}>{inr(b.balance_due)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <EmailDialog
                      endpoint={`/bills/${b.id}/email`}
                      testIdPrefix={`email-bill-${b.id}`}
                      defaultTo={buildEmailDefaults(b).to}
                      defaultSubject={buildEmailDefaults(b).subject}
                      defaultMessage={buildEmailDefaults(b).message}
                      trigger={
                        <Button variant="ghost" size="sm" className="rounded-sm h-7 text-brand-forest" data-testid={`email-bill-btn-${b.id}`}>
                          <Mail className="h-3.5 w-3.5 mr-1" /> Email
                        </Button>
                      }
                    />
                    <Link to={`/bills/${b.id}`}>
                      <Button variant="ghost" size="sm" className="rounded-sm h-7" data-testid={`view-bill-${b.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> View / Edit
                      </Button>
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
